import { BrowserSystemOptions, Configuration, LogLevel } from '@azure/msal-browser';
import Tenant from 'models/tenant';
import AppError from 'utils/appError';
import Config from '../Config/configService';
import logger from '../Logging/logService';

//
// Client Id's for consent requests
//
export const apiClientId = Config.get('Api.ClientId') as string;
export const svcClientId = Config.get('Svc.ClientId') as string;
export const entraIdClientId = Config.get('EntraID.ClientId') as string;
export const appClientId = Config.get('App.ClientId') as string;
export const plannerClientId = Config.get('Planner.ClientId') as string;

//
// MSAL Configuration
//
const browserSystemOptions: BrowserSystemOptions = {
  loggerOptions: {
    loggerCallback: (level: LogLevel, message: string, containsPii: boolean): void => {
      if (containsPii) {
        return;
      }
      switch (level) {
        case LogLevel.Warning:
        case LogLevel.Error:
          logger.error(new AppError(message));

          return;
        case LogLevel.Info:
          logger.info(message);

          return;
        case LogLevel.Verbose:
          logger.debug(message);

          return;
      }
    },
    piiLoggingEnabled: false,
  },
  windowHashTimeout: 15000,
  iframeHashTimeout: 15000,
  loadFrameTimeout: 15000,
};

export const msalConfigCommon: Configuration = {
  auth: {
    clientId: appClientId,
    authority: 'https://login.microsoftonline.com/common',
    redirectUri: Config.getAppURL(),
    postLogoutRedirectUri: Config.getAppURL() + '/logout',
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: true, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: browserSystemOptions,
};

export const getMsalConfigForTenant = (tenant: Tenant): Configuration => {
  return {
    auth: {
      clientId: appClientId,
      authority: `https://login.microsoftonline.com/${tenant.id}`,
      redirectUri: Config.getAppURL(),
      postLogoutRedirectUri: Config.getAppURL() + '/logout',
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: true, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: browserSystemOptions,
  };
};

//
// Scopes for different requests
//
export enum GraphScopeId {
  Pages = 1,
  Library = 2,
  SiteOwner = 3,
}

/**
 * Add here the scopes to request when obtaining an access token for the basic login
 */
export const loginRequest = {
  scopes: ['User.Read'],
};

/**
 * Add here the scopes to request when obtaining an access token for user management
 */
export const userMgmtRequest = {
  scopes: ['User.Read', 'User.ReadBasic.All'],
};

/**
 * Add here the scopes to request when obtaining an access token for the API.
 */
export const apiRequest = {
  scopes: ['offline_access', `${apiClientId}/.default`],
};

/**
 * Microsoft Secure Scores
 */
export const azureManagementRequest = {
  scopes: ['https://management.azure.com/user_impersonation'],
};

export const graphSecureScoresRequest = {
  scopes: ['https://graph.microsoft.com/SecurityEvents.Read.All'],
};

export const graphDirectoryRecommendations = {
  scopes: ['https://graph.microsoft.com/DirectoryRecommendations.Read.All'],
};

export const defenderForEndpointRequest = {
  scopes: [
    'https://api.securitycenter.microsoft.com/Score.Read',
    'https://api.securitycenter.microsoft.com/SecurityRecommendation.Read',
  ],
};

/**
 * Sharepoint integration scopes
 */
export const graphSharepointSelectedRequest = {
  scopes: ['Sites.Selected', 'Lists.SelectedOperations.Selected'],
};

export const graphSharepointLibraryRequest = {
  scopes: ['Sites.Read.All', 'Files.ReadWrite.All'],
};

export const graphSharepointPagesRequest = {
  scopes: ['Sites.Read.All'],
};

export const graphSharepointManageRequest = {
  scopes: ['Sites.Manage.All', 'Sites.Read.All', 'Files.ReadWrite.All'],
};

export const graphSharepointSearchConsent = {
  scopes: ['Sites.Read.All', 'Files.Read.All'],
};

export const graphSharepointReportsConsent = {
  scopes: ['https://graph.microsoft.com/Files.ReadWrite.All'],
};

/*
 * Teams integration scopes
 */
export const graphTeamsChannelRequest = {
  scopes: ['ChannelMessage.Send'],
};

export const graphTeamsCollaborationRequest = {
  scopes: ['Chat.Create', 'Channel.Create', 'ChatMessage.Send', 'ChannelMessage.Send'],
};

export const graphTeamsMembersRequest = {
  scopes: ['TeamMember.Read.All', 'User.Read'],
};

export const graphTeamsTaskConsent = {
  scopes: ['TeamMember.Read.All', 'User.Read', 'Sites.Read.All', 'Files.ReadWrite.All'],
};

export const graphTeamsLibraryConsent = {
  scopes: ['TeamMember.Read.All', 'User.Read', 'Sites.Read.All', 'Files.ReadWrite.All', 'ChannelMessage.Send'],
};

/*
 * Outlook integration scopes
 */
export const graphOutlookTasksBasicRequest = {
  scopes: [
    'https://graph.microsoft.com/User.Read.All',
    'https://graph.microsoft.com/Group.Read.All',
    'https://graph.microsoft.com/Calendars.ReadWrite',
  ],
};

export const graphOutlookTasksAdvancedRequest = {
  scopes: [`${apiClientId}/.default`],
};

/*
 * Microsoft Entra ID integration scopes
 */
export const graphADConsentRequest = {
  scopes: ['https://graph.microsoft.com/User.Read.All', 'https://graph.microsoft.com/Group.Read.All'],
};

export const entraIdConsentRequest = {
  scopes: ['offline_access', `${entraIdClientId}/.default`],
};

/*
 * Power Automate
 */
export const powerAutomateSharePointConsent = {
  scopes: ['https://graph.microsoft.com/Files.Read.All', 'https://graph.microsoft.com/Sites.Read.All'],
};

/*
 * Power BI
 */
export const powerBIServiceRequest = {
  scopes: ['openid', 'profile', 'email'],
};

export const powerBIClientRequest = {
  scopes: [
    'openid',
    'profile',
    'email',
    'https://analysis.windows.net/powerbi/api/Report.Read.All',
    'https://analysis.windows.net/powerbi/api/UserState.ReadWrite.All',
  ],
};

/*
 * Planner
 */
export const plannerRequest = {
  scopes: ['offline_access', `${plannerClientId}/.default`],
};

/*
 * Merge default scopes
 */
export const mergeScopes = (scopes: string[][]): string[] => {
  const mergedScopes = new Set<string>();
  scopes.forEach((scopeArray) => {
    scopeArray.forEach((scope) => mergedScopes.add(scope));
  });

  return Array.from(mergedScopes);
};

export const defaultScopes = mergeScopes([
  loginRequest.scopes,
  userMgmtRequest.scopes,
  graphSharepointPagesRequest.scopes,
  graphSharepointLibraryRequest.scopes,
]);
